import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Calendar } from "primereact/calendar";

import motorIcon from "assets/svg/claims/motor-black-icon.svg";
import motorActiveIcon from "assets/svg/claims/motor-active-icon.svg";
import medicalIcon from "assets/svg/claims/health.svg";
import medicalActiveIcon from "assets/svg/claims/health-active.svg";
import { Alshamal, SanadIcon } from "assets/svg/motor/ValueAddedIcons";
import colors from "assets/scss/abstracts/colors.scss";
import HeaderStickyMenu from "component/common/MobileReuseable/HeaderStickyMenu";

import * as globalUtils from "./utils";
import { externalLinks } from "helpers/constant";
import { getClaimTrakerDetails } from "service/dashboard/dashboardService";
import {
  updateLoader,
  updateMotorClaimTrackDetails,
} from "action/DashboardInformation";

import "./style.scss";

const CircleArrowIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="16" cy="16" r="16" fill="#6c5ce7" />
    <path
      d="M14 10L20 16L14 22"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClaimsForm = ({ onTrack, fullWidth = false, customWidth, setMenuOpen = () => {} }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [mainTab, setMainTab] = useState("motor");
  const [subTab, setSubTab] = useState("alShamel");

  const [formData, setFormData] = useState({
    nationalId: "",
    policyNumber: "",
    claimNumber: "",
    referenceNumber: "",
    najmNumber: "",
    birthYear: null,
    accidentReportNumber: "",
    accidentDate: null,
    cardNumber: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const selectedLanguage = localStorage.getItem("lang");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => globalUtils.deleteByKey(name, prev));
    }
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => globalUtils.deleteByKey(name, prev));
    }
  };

  const calLem = (url) => {
    if (window.lmSMTObj) {
      window.lmSMTObj.page(url, {
        path: document.location.pathname,
        language: selectedLanguage === "ar" ? "Arabic" : "English",
        referrer: document.referrer,
        title: document.title,
        url: document.URL,
        search: location.search,
      });
    }
  };

  const trackEventWithProduct = (tabClicked, product) => {
    if (window.lmSMTObj) {
      window.lmSMTObj.track(tabClicked, { Product: product });
    }
  };

  const validateAlShamel = () => {
    let errors = {};
    if (!globalUtils.isValidSaudiIDOrIQama(formData.nationalId)) {
      errors.nationalId = t("claimsPage.errors.nationalIdInvalid");
    }
    if (!globalUtils.isPolicyNumber(formData.policyNumber)) {
      errors.policyNumber = t("claimsPage.errors.policyNumberInvalid");
    }
    if (!globalUtils.isClaimNumber(formData.claimNumber)) {
      errors.claimNumber = t("claimsPage.errors.claimNumberInvalid");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateThirdParty = () => {
    let errors = {};
    if (!globalUtils.isValidSaudiIDOrIQama(formData.nationalId)) {
      errors.nationalId = t("claimsPage.errors.nationalIdInvalid");
    }
    if (!globalUtils.isPolicyNumber(formData.referenceNumber)) {
      errors.referenceNumber = t("claimsPage.errors.referenceNumberInvalid");
    }
    if (!globalUtils.isClaimNumber(formData.najmNumber)) {
      errors.najmNumber = t("claimsPage.errors.najmNumberInvalid");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateUploadDocs = () => {
    let errors = {};
    if (!globalUtils.isValidSaudiIDOrIQama(formData.nationalId)) {
      errors.nationalId = t("claimsPage.errors.nationalIdInvalid");
    }
    if (!formData.birthYear) {
      errors.birthYear = t("claimsPage.errors.birthYearRequired");
    }
    if (!globalUtils.isClaimNumber(formData.claimNumber)) {
      errors.claimNumber = t("claimsPage.errors.claimNumberInvalid");
    }
    if (!formData.accidentReportNumber) {
      errors.accidentReportNumber = t("claimsPage.errors.accidentReportNumberRequired");
    }
    if (!formData.accidentDate) {
      errors.accidentDate = t("claimsPage.errors.accidentDateRequired");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateMedical = () => {
    let errors = {};
    if (!globalUtils.isPolicyNumber(formData.policyNumber)) {
      errors.policyNumber = t("claimsPage.errors.policyNumberInvalid");
    }
    if (!globalUtils.isPolicyNumber(formData.cardNumber)) {
      errors.cardNumber = t("claimsPage.errors.cardNumberInvalid");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAlShamelSubmit = () => {
    if (!validateAlShamel()) return;
    const data = {
      langCode: i18n.language.slice(0, 1).toUpperCase(),
      policyNumber: formData.policyNumber,
      idNumber: formData.nationalId,
      claimNumber: formData.claimNumber,
      claimDetailNumber: "",
      najmNumber: "",
      cardCode: "",
      claimType: "MOTOR",
    };

    dispatch(updateLoader(true));
    getClaimTrakerDetails(data)
      .then((response) => {
        if (response?.return?.status === "SUCCESS") {
          setMenuOpen(null);
          dispatch(
            updateMotorClaimTrackDetails({
              ...response.return.data,
              policyNumber: formData.policyNumber,
              claimNumber: formData.claimNumber,
            })
          );
        }
      })
      .finally(() => {
        dispatch(updateLoader(false));
        history.push("/claims/motor");
      });
  };

  const handleThirdPartySubmit = () => {
    if (!validateThirdParty()) return;
    const data = {
      langCode: i18n.language.slice(0, 1).toUpperCase(),
      policyNumber: formData.referenceNumber,
      idNumber: formData.nationalId,
      claimNumber: "1",
      claimDetailNumber: "",
      najmNumber: formData.najmNumber,
      cardCode: "",
      claimType: "MOTOR",
    };

    dispatch(updateLoader(true));
    getClaimTrakerDetails(data)
      .then((response) => {
        dispatch(
          updateMotorClaimTrackDetails({
            ...response.return.data,
            policyNumber: formData.referenceNumber,
            claimNumber: formData.referenceNumber,
          })
        );
      })
      .finally(() => {
        dispatch(updateLoader(false));
        history.push("/claims/motor");
      });
  };

  const handleUploadDocsSubmit = () => {
    if (!validateUploadDocs()) return;
    const yob = formData.birthYear ? formData.birthYear.getFullYear() : "";
    const dateFormatted = formData.accidentDate ? formData.accidentDate.toString() : "";
    window.location.href = `${process.env.REACT_APP_PURCHASE_URL}tp-claims?co=false&type=upload&claim=${formData.claimNumber}&report=${formData.accidentReportNumber}&lang=${i18n.language}&date=${dateFormatted}&id=${formData.nationalId}&yob=${yob}`;
  };

  const handleMedicalSubmit = () => {
    if (!validateMedical()) return;
    window.location.assign("/login");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onTrack) onTrack({ mainTab, subTab, ...formData });
    if (mainTab === "motor") {
      if (subTab === "alShamel") handleAlShamelSubmit();
      else if (subTab === "thirdParty") handleThirdPartySubmit();
      else if (subTab === "uploadDoc") handleUploadDocsSubmit();
    } else if (mainTab === "medical") {
      handleMedicalSubmit();
    }
  };

  const containerStyle = {
    maxWidth: fullWidth ? "100%" : customWidth || "552px",
    width: "100%",
    margin: "0 auto",
  };

  return (
    <div className="claims-page-wrapper">
      {/* Mobile Sticky Navigation Header */}
      {window.innerWidth < 768 && <HeaderStickyMenu profileClass={true} />}

      {/* Production Top Claims Title Box */}
      {window.innerWidth < 600 && (
        <div
          className="claims-header-box"
          style={{
            background: "#ffffff",
            boxShadow: "0 4px 10px rgba(9, 30, 66, 0.06)",
            borderRadius: "0 0 12px 12px",
            padding: "1rem 1.25rem",
            marginBottom: "1rem",
            marginTop: "1rem",
          }}
        >
          <h2 className="claims-title" style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0, color: "#1a1c1f" }}>
            {t("claimsPage.claims")}
          </h2>
        </div>
      )}

      {/* Outer Padding Container */}
      <div className="claims-content-container px-3 pb-4">
        {/* Hope Info Banner */}
        {window.innerWidth < 600 && (
          <div className="claims-hope-banner mb-4">
            <h3 className="hope-title" style={{ fontWeight: 800, fontSize: "1.5rem", color: "#1a1c1f", marginBottom: "0.25rem" }}>
              {t("claimsPage.hopeYoureOk")}
            </h3>
            <p className="hope-subtitle" style={{ fontWeight: 400, fontSize: "0.9rem", color: "#455560", margin: 0 }}>
              {t("claimsPage.availableText")}
            </p>
          </div>
        )}

        {/* Quick Redirect Cards Section */}
        {window.innerWidth < 600 && (
          <div className="submit-track-section mb-4">
            <h4 className="submit-track-title mb-3" style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1a1c1f" }}>
              {t("claimsPage.submitAndTrackClaims")}
            </h4>

            <div className="redirect-cards-container d-flex flex-column gap-3">
              <div
                className="redirect-card p-3 rounded-4 border bg-white position-relative cursor-pointer"
                onClick={() => {
                  trackEventWithProduct(t("claimsPage.alShamel") + t("claimsPage.sanadPlus") + "Clicked", "Claims");
                }}
              >
                <div className="card-icon mb-2">
                  <Alshamal />
                </div>
                <div className="card-content">
                  <h5 className="card-title mb-1 fw-bold">
                    {t("claimsPage.alShamel")} & {t("claimsPage.thirdPartyPlusText")}
                  </h5>
                  <p className="card-desc text-muted mb-3">
                    {t("claimsPage.comprehensiveCarsInsurance")}
                  </p>
                </div>
                <div className="card-action-btn">
                  <CircleArrowIcon />
                </div>
              </div>

              <div
                className="redirect-card p-3 rounded-4 border bg-white position-relative cursor-pointer"
                onClick={() => {
                  calLem(window.location.assign(externalLinks.common + "tp-claims"));
                  trackEventWithProduct(t("claimsPage.sanad") + "Clicked", "Claims");
                }}
              >
                <div className="card-icon mb-2">
                  <SanadIcon fill={colors["bright-purple"]} />
                </div>
                <div className="card-content">
                  <h5 className="card-title mb-1 fw-bold">{t("claimsPage.sanad")}</h5>
                  <p className="card-desc text-muted mb-3">
                    {t("claimsPage.thirdPartyLiabilityClaims")}
                  </p>
                </div>
                <div className="card-action-btn">
                  <CircleArrowIcon />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Title for Form */}
        {window.innerWidth < 600 && (
          <h4 className="submit-track-title mb-3" style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1a1c1f" }}>
            {t("claimsPage.trackYourClaim")}
          </h4>
        )}

        {/* Card Section */}
        <div className={`check-claim-status-card ${fullWidth ? "full-width" : ""}`} style={containerStyle}>
          {/* Main Tabs */}
          <div className="main-tabs-container">
            <button
              type="button"
              className={`main-tab-btn ${mainTab === "motor" ? "active" : ""}`}
              onClick={() => {
                setMainTab("motor");
                setFormErrors({});
              }}
            >
              <span className="dot-indicator" />
              <img 
                src={mainTab === "motor" ? motorActiveIcon : motorIcon} 
                alt="Motor" 
                className="tab-icon" 
              />
              <span>{t("claimsPage.tabs.motor")}</span>
            </button>

            <button
              type="button"
              className={`main-tab-btn ${mainTab === "medical" ? "active" : ""}`}
              onClick={() => {
                setMainTab("medical");
                setFormErrors({});
              }}
            >
              <span className="dot-indicator" />
              <img 
                src={mainTab === "medical" ? medicalActiveIcon : medicalIcon} 
                alt="Medical" 
                className="tab-icon" 
              />
              <span>{t("claimsPage.tabs.medical")}</span>
            </button>
          </div>

          {/* Sub Tabs */}
          {mainTab === "motor" && (
            <div className="sub-tabs-container">
              <button
                type="button"
                className={`sub-tab-pill ${subTab === "alShamel" ? "active" : ""}`}
                onClick={() => {
                  setSubTab("alShamel");
                  setFormErrors({});
                }}
              >
                {t("claimsPage.subTabs.alShamel")}
              </button>

              <button
                type="button"
                className={`sub-tab-pill ${subTab === "thirdParty" ? "active" : ""}`}
                onClick={() => {
                  setSubTab("thirdParty");
                  setFormErrors({});
                }}
              >
                {t("claimsPage.subTabs.thirdParty")}
              </button>

              <button
                type="button"
                className={`sub-tab-pill ${subTab === "uploadDoc" ? "active" : ""}`}
                onClick={() => {
                  setSubTab("uploadDoc");
                  setFormErrors({});
                }}
              >
                {t("claimsPage.subTabs.uploadDoc")}
              </button>
            </div>
          )}

          {/* Form Area */}
          <form onSubmit={handleSubmit} className="claim-form">
            {mainTab === "motor" && subTab === "alShamel" && (
              <>
                <div className="form-group">
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.nationalId")}
                  />
                  {formErrors.nationalId && <small className="p-error block">{formErrors.nationalId}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.policyNumber")}
                  />
                  {formErrors.policyNumber && <small className="p-error block">{formErrors.policyNumber}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="claimNumber"
                    value={formData.claimNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.claimNumber")}
                  />
                  {formErrors.claimNumber && <small className="p-error block">{formErrors.claimNumber}</small>}
                </div>
              </>
            )}

            {mainTab === "motor" && subTab === "thirdParty" && (
              <>
                <div className="form-group">
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.nationalId")}
                  />
                  {formErrors.nationalId && <small className="p-error block">{formErrors.nationalId}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="referenceNumber"
                    value={formData.referenceNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.referenceNumber")}
                  />
                  {formErrors.referenceNumber && <small className="p-error block">{formErrors.referenceNumber}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="najmNumber"
                    value={formData.najmNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.najmNumber")}
                  />
                  {formErrors.najmNumber && <small className="p-error block">{formErrors.najmNumber}</small>}
                </div>
              </>
            )}

            {mainTab === "motor" && subTab === "uploadDoc" && (
              <>
                <div className="form-group">
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.nationalId")}
                  />
                  {formErrors.nationalId && <small className="p-error block">{formErrors.nationalId}</small>}
                </div>
                <div className="form-group">
                  <Calendar
                    className="w-100"
                    inputId="birthYear"
                    value={formData.birthYear}
                    onChange={(e) => handleDateChange("birthYear", e.value)}
                    placeholder={t("claimsPage.placeholders.birthYear")}
                    view="year"
                    dateFormat="yy"
                    yearNavigator
                    yearRange="1900:2026"
                  />
                  {formErrors.birthYear && <small className="p-error block">{formErrors.birthYear}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="claimNumber"
                    value={formData.claimNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.claimNumber")}
                  />
                  {formErrors.claimNumber && <small className="p-error block">{formErrors.claimNumber}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="accidentReportNumber"
                    value={formData.accidentReportNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.accidentReportNumber")}
                  />
                  {formErrors.accidentReportNumber && <small className="p-error block">{formErrors.accidentReportNumber}</small>}
                </div>
                <div className="form-group">
                  <Calendar
                    className="w-100"
                    inputId="accidentDate"
                    value={formData.accidentDate}
                    onChange={(e) => handleDateChange("accidentDate", e.value)}
                    placeholder={t("claimsPage.placeholders.accidentDate")}
                    dateFormat="dd/mm/yy"
                    maxDate={new Date()}
                  />
                  {formErrors.accidentDate && <small className="p-error block">{formErrors.accidentDate}</small>}
                </div>
              </>
            )}

            {mainTab === "medical" && (
              <>
                <div className="form-group">
                  <input
                    type="text"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.policyNumber")}
                  />
                  {formErrors.policyNumber && <small className="p-error block">{formErrors.policyNumber}</small>}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleInputChange}
                    placeholder={t("claimsPage.placeholders.cardNumber")}
                  />
                  {formErrors.cardNumber && <small className="p-error block">{formErrors.cardNumber}</small>}
                </div>
              </>
            )}

            <button type="submit" className="submit-track-btn">
              {subTab === "uploadDoc" && mainTab === "motor"
                ? t("claimsPage.confirmBtn")
                : t("claimsPage.trackBtn")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClaimsForm;